"use client";

import React, { useRef, useState, useEffect } from "react";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { Link } from "react-router-dom";
import Logo from "../assets/images/saelogo.png";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false); // New state to track scrolling
  
  const navbarRef = useRef(null);
  const menuRef = useRef(null);
  const linksRef = useRef([]);

  // Scroll Event Listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    
    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Lock background scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      // Use an empty string to cleanly remove the inline style
      document.body.style.overflow = '';
    }
    
    // Cleanup in case component unmounts while menu is open
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useGSAP(() => {
    // Initial Navbar Entry
    gsap.from(navbarRef.current, {
      opacity: 0,
      y: -80,
      duration: 0.6,
      ease: "power3.out",
    });
  }, []);

  // Menu Animation Logic
  useGSAP(() => {
    if (menuOpen) {
      // Background Slide Down
      gsap.to(menuRef.current, {
        y: "0%",
        duration: 0.7,
        ease: "power4.inOut",
      });
      // Staggered Link Entry
      gsap.fromTo(
        linksRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.1, duration: 0.5, ease: "power3.out", delay: 0.4 }
      );
    } else {
      gsap.to(menuRef.current, {
        y: "-100%",
        duration: 0.6,
        ease: "power4.inOut",
      });
    }
  }, [menuOpen]);

  const navLinks = ["HOME", "EVENTS", "TEAMS", "TEDX", "AAROHAN", "ALUMNI"];

  return (
    // Replaced the empty <> fragment with a stable <header> wrapper
    <header className="relative z-[100]">
      <nav
        ref={navbarRef}
        className={`fixed top-0 z-[100] w-full transition-colors duration-300 ${
          isScrolled && !menuOpen ? "bg-black shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="w-full px-8 py-2.5 flex justify-between items-center">
          <Link 
            to="/" 
            onClick={() => window.scrollTo(0, 0)}
          >
            <img src={Logo} alt="SAE Logo" className="h-10 lg:h-12 w-auto object-contain" />
          </Link>

          {/* Hamburger / Close Icon */}
          <div
            onClick={() => setMenuOpen(!menuOpen)}
            className="relative z-[110] w-10 h-6 cursor-pointer flex flex-col justify-between items-end"
          >
            <span className={`h-[2px] bg-white transition-all duration-500 ${menuOpen ? "w-10 rotate-45 translate-y-2.5" : "w-10"}`} />
            <span className={`h-[2px] bg-white transition-all duration-300 ${menuOpen ? "opacity-0" : "w-6"}`} />
            <span className={`h-[2px] bg-white transition-all duration-500 ${menuOpen ? "w-10 -rotate-45 -translate-y-2.5" : "w-10"}`} />
          </div>
        </div>
      </nav>

      {/* Fullscreen Menu Overlay */}
      <div
        ref={menuRef}
        className="fixed inset-0 z-[90] bg-black flex flex-col items-center justify-center -translate-y-full"
      >
        <div className="flex flex-col items-center gap-6 lg:gap-10">
          {navLinks.map((item, index) => (
            <Link
              key={item}
              ref={(el) => (linksRef.current[index] = el)}
              to={item === "HOME" ? "/" : `/${item.toLowerCase()}`}
              onClick={() => {
                setMenuOpen(false);
                window.scrollTo(0, 0); // Force scroll to top here too!
              }}
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tighter hover:text-red-600 transition-colors no-underline uppercase"
            >
              {item}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Navbar;